import { useState, type FormEvent } from "react";
import { Building2, CheckCircle2, Eye, EyeOff, LoaderCircle, MailCheck, ShieldCheck, UserPlus } from "lucide-react";
import type { Session } from "../api.js";
import {
  productApi,
  type ProductApi,
  type ProductBusiness,
  type ProductSession
} from "../product-api.js";

type AuthMode = "SIGN_IN" | "SIGN_UP" | "CONFIRM";

type SignupDraft = {
  email: string;
  password: string;
  displayName: string;
  businessName: string;
  businessSlug: string;
  timezone: string;
};

export function ProductOnboarding({
  onLogin,
  legacyLogin,
  loadWorkspaceSession,
  initialBusinessSlug = "",
  apiClient = productApi
}: {
  onLogin: (session: Session) => void;
  legacyLogin: (login: string, password: string) => Promise<Session>;
  loadWorkspaceSession: () => Promise<Session>;
  initialBusinessSlug?: string;
  apiClient?: ProductApi;
}) {
  const [mode, setMode] = useState<AuthMode>("SIGN_IN");
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [businesses, setBusinesses] = useState<readonly ProductBusiness[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [signup, setSignup] = useState<SignupDraft>({
    email: "",
    password: "",
    displayName: "",
    businessName: "",
    businessSlug: "",
    timezone: browserTimezone()
  });
  const [confirmationToken, setConfirmationToken] = useState("");

  const changeMode = (next: AuthMode) => {
    setMode(next);
    setError("");
    setStatus("");
    setBusinesses([]);
  };

  const finishProductLogin = async (businessSlug?: string) => {
    const result = await apiClient.login(login, password, businessSlug);
    if (isBusinessSelection(result)) {
      setBusinesses(result.businesses);
      setStatus("Choose the business you want to open.");
      return;
    }
    try {
      onLogin(await loadWorkspaceSession());
    } catch {
      throw new Error("This business account is valid, but its operations workspace is not available in this deployment.");
    }
  };

  const submitLogin = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setStatus("");
    try {
      // WHY [BOL-PRODUCT-LOGIN-COMPAT-001]: product accounts are email based,
      // while the accepted production desk still supports a short owner login.
      // Probe product auth only for an email and fall back only on literal
      // invalid credentials; never turn a business-selection or workspace error
      // into a different account login. Falsifier: a short owner can no longer
      // sign in, or a valid product account silently enters the legacy account.
      if (!looksLikeEmail(login)) {
        if (initialBusinessSlug) throw new Error("Enter the email address for the business account you want to open.");
        onLogin(await legacyLogin(login, password));
        return;
      }
      try {
        await finishProductLogin(initialBusinessSlug || undefined);
      } catch (cause) {
        if (!isInvalidCredentials(cause) || initialBusinessSlug) throw cause;
        onLogin(await legacyLogin(login, password));
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Login or password is incorrect.");
    } finally {
      setBusy(false);
    }
  };

  const chooseBusiness = async (business: ProductBusiness) => {
    setBusy(true);
    setError("");
    setStatus(`Opening ${business.name}.`);
    try {
      await finishProductLogin(business.slug);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "That business could not be opened.");
      setStatus("");
    } finally {
      setBusy(false);
    }
  };

  const submitSignup = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setStatus("");
    try {
      const result = await apiClient.signup(signup);
      setStatus(signupDeliveryMessage(result.deliveryAdapter));
      setConfirmationToken("");
      setMode("CONFIRM");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The account could not be created.");
    } finally {
      setBusy(false);
    }
  };

  const submitConfirmation = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await apiClient.confirmEmail(confirmationToken.trim());
      setStatus("Email confirmed. Sign in to open your business.");
      setMode("SIGN_IN");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "That confirmation token is invalid or expired.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="login-page login-page--simple product-onboarding">
      <section className="login-simple" aria-label="Business Ops account access">
        <div className="login-simple__brand"><span className="brand-mark" aria-hidden="true"><span /></span><span><strong>Production desk</strong><small>Business Ops Loop</small></span></div>
        <div className="product-auth-card">
          <nav className="product-auth-tabs" aria-label="Account options">
            <button type="button" aria-pressed={mode === "SIGN_IN"} onClick={() => changeMode("SIGN_IN")}>Sign in</button>
            <button type="button" aria-pressed={mode === "SIGN_UP"} onClick={() => changeMode("SIGN_UP")}>Create account</button>
            <button type="button" aria-pressed={mode === "CONFIRM"} onClick={() => changeMode("CONFIRM")}>Confirm email</button>
          </nav>

          {mode === "SIGN_IN" ? <form className="product-auth-form" onSubmit={(event) => void submitLogin(event)}>
            <span className="login-form__shield"><ShieldCheck size={23} aria-hidden="true" /></span>
            <p className="eyebrow">Private workspace</p>
            <h1>{initialBusinessSlug ? "Switch business" : "Sign in"}</h1>
            <p className="product-auth-form__intro">{initialBusinessSlug ? `Re-enter your details to open ${initialBusinessSlug}.` : "Open the business where your team, work, and connected accounts belong."}</p>
            <label><span>Email or login</span><input type="text" autoComplete="username" required value={login} onChange={(event) => setLogin(event.target.value)} /></label>
            <label><span>Password</span><span className="password-field"><input type={showPassword ? "text" : "password"} autoComplete="current-password" required value={password} onChange={(event) => setPassword(event.target.value)} /><button type="button" onClick={() => setShowPassword((visible) => !visible)} aria-label={showPassword ? "Hide password" : "Show password"}>{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button></span></label>
            {businesses.length > 0 ? <fieldset className="business-choice"><legend>Choose a business</legend>{businesses.map((business) => <button key={business.id} type="button" disabled={busy} onClick={() => void chooseBusiness(business)}><span className="business-choice__icon"><Building2 size={18} aria-hidden="true" /></span><span><strong>{business.name}</strong><small>{business.slug} · {roleLabel(business.role)} · {business.timezone}</small></span></button>)}</fieldset> : null}
            {error ? <p className="form-error" role="alert">{error}</p> : null}
            {status ? <p className="form-status" role="status">{status}</p> : null}
            <button className="primary-button login-submit" type="submit" disabled={busy || businesses.length > 0}>{busy ? <><LoaderCircle className="spin" size={18} />Signing in…</> : businesses.length > 0 ? "Choose a business above" : "Sign in"}</button>
          </form> : null}

          {mode === "SIGN_UP" ? <form className="product-auth-form" onSubmit={(event) => void submitSignup(event)}>
            <span className="login-form__shield"><UserPlus size={23} aria-hidden="true" /></span>
            <p className="eyebrow">New account</p>
            <h1>Create your first business</h1>
            <p className="product-auth-form__intro">Your account can own more than one business. Each business keeps its people, connections, and work separate.</p>
            <div className="product-auth-grid">
              <label><span>Your name</span><input type="text" autoComplete="name" required maxLength={160} value={signup.displayName} onChange={(event) => setSignup((current) => ({ ...current, displayName: event.target.value }))} /></label>
              <label><span>Email</span><input type="email" autoComplete="email" required maxLength={320} value={signup.email} onChange={(event) => setSignup((current) => ({ ...current, email: event.target.value }))} /></label>
              <label><span>Business name</span><input type="text" autoComplete="organization" required maxLength={160} value={signup.businessName} onChange={(event) => {
                const businessName = event.target.value;
                setSignup((current) => ({ ...current, businessName, businessSlug: slugEdited ? current.businessSlug : businessSlugFromName(businessName) }));
              }} /></label>
              <label><span>Business slug</span><input type="text" inputMode="url" required pattern="[a-z0-9][a-z0-9-]{1,62}" aria-describedby="signup-slug-help" value={signup.businessSlug} onChange={(event) => { setSlugEdited(true); setSignup((current) => ({ ...current, businessSlug: event.target.value.toLowerCase() })); }} /><small id="signup-slug-help">Lowercase letters, numbers, and hyphens.</small></label>
              <label><span>Timezone</span><input type="text" required maxLength={80} value={signup.timezone} onChange={(event) => setSignup((current) => ({ ...current, timezone: event.target.value }))} /></label>
              <label><span>Password</span><input type="password" autoComplete="new-password" required minLength={15} maxLength={256} aria-describedby="signup-password-help" value={signup.password} onChange={(event) => setSignup((current) => ({ ...current, password: event.target.value }))} /><small id="signup-password-help">Use at least 15 characters.</small></label>
            </div>
            {error ? <p className="form-error" role="alert">{error}</p> : null}
            <button className="primary-button login-submit" type="submit" disabled={busy}>{busy ? <><LoaderCircle className="spin" size={18} />Creating account…</> : "Create account"}</button>
          </form> : null}

          {mode === "CONFIRM" ? <form className="product-auth-form" onSubmit={(event) => void submitConfirmation(event)}>
            <span className="login-form__shield"><MailCheck size={23} aria-hidden="true" /></span>
            <p className="eyebrow">Email confirmation</p>
            <h1>Confirm your email</h1>
            <p className="product-auth-form__intro">Open the confirmation link from your email, or enter the token an administrator gave you.</p>
            <label><span>Confirmation token</span><textarea rows={4} required minLength={32} maxLength={512} autoComplete="one-time-code" value={confirmationToken} onChange={(event) => setConfirmationToken(event.target.value)} /></label>
            {error ? <p className="form-error" role="alert">{error}</p> : null}
            {status ? <p className="form-status" role="status"><CheckCircle2 size={17} aria-hidden="true" />{status}</p> : null}
            <button className="primary-button login-submit" type="submit" disabled={busy}>{busy ? <><LoaderCircle className="spin" size={18} />Confirming…</> : "Confirm email"}</button>
          </form> : null}
        </div>
        <small className="login-fineprint">Private workspace · Activity is recorded for accountability</small>
      </section>
    </main>
  );
}

export function businessSlugFromName(name: string): string {
  return name.trim().toLowerCase().normalize("NFKD")
    .replace(/[\u0300-\u036f]/gu, "")
    .replace(/[^a-z0-9]+/gu, "-")
    .replace(/^-+|-+$/gu, "")
    .slice(0, 63);
}

export function signupDeliveryMessage(adapter: "CONFIGURED" | "UNAVAILABLE"): string {
  // WHY [BOL-PRODUCT-SIGNUP-TRUTH-001]: signup deliberately returns the same
  // acceptance response for new and existing addresses. Copy must preserve
  // that anti-enumeration boundary instead of claiming an account was created.
  // Falsifier: the public response reveals whether an email already exists.
  return adapter === "CONFIGURED"
    ? "If this email can be registered, check its inbox for a confirmation link."
    : "The request was accepted, but email delivery is not configured. An administrator must provide a confirmation token for a new account.";
}

export function isBusinessSelection(value: ProductSession | { selectionRequired: true; businesses: readonly ProductBusiness[] }): value is { selectionRequired: true; businesses: readonly ProductBusiness[] } {
  return "selectionRequired" in value && value.selectionRequired === true;
}

function looksLikeEmail(value: string): boolean {
  return /^\S+@\S+\.\S+$/u.test(value.trim());
}

function isInvalidCredentials(value: unknown): boolean {
  return typeof value === "object" && value !== null
    && (("status" in value && value.status === 401) || ("code" in value && value.code === "INVALID_CREDENTIALS"));
}

function roleLabel(role: ProductBusiness["role"]): string {
  return role.charAt(0) + role.slice(1).toLowerCase();
}

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}
